import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, Users, Bot, BookOpen, DollarSign, Settings, TrendingUp, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalUsers: 0, totalAgents: 0, totalCases: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'operator') {
      navigate('/create');
      return;
    }
    if (user?.role === 'operator') {
      navigate('/admin/cases', { replace: true });
      return;
    }
    fetchStats();
  }, [user?.role, navigate]);

  const fetchStats = async () => {
    try {
      const [usersRes, agentsRes, casesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users?page=1&limit=1`),
        axios.get(`${API_URL}/api/admin/agents`),
        axios.get(`${API_URL}/api/case-library/admin/cases`).catch(() => ({ data: [] })),
      ]);
      setStats({
        totalUsers: usersRes.data.total || 0,
        totalAgents: agentsRes.data.length || 0,
        totalCases: casesRes.data.length || 0,
      });
    } catch (error) {
      console.error('獲取統計數據失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { title: '數據總覽', description: '查看平台使用數據與統計圖表', icon: BarChart3, path: '/admin/stats', roles: ['admin'] },
    { title: '用戶管理', description: '管理用戶帳號與權限', icon: Users, path: '/admin/users', stat: stats.totalUsers, statLabel: '位用戶', roles: ['admin'] },
    { title: 'Agent 管理', description: '配置 AI Agent、子代理與審核系統', icon: Bot, path: '/admin/agents', stat: stats.totalAgents, statLabel: '個 Agent', roles: ['admin'] },
    { title: '案例庫', description: '管理爆款影片案例與拆解分析', icon: BookOpen, path: '/admin/cases', stat: stats.totalCases, statLabel: '個案例', roles: ['admin', 'operator'] },
    { title: '計費管理', description: '點數方案、定價與付款記錄', icon: DollarSign, path: '/admin/credit-plans', roles: ['admin'] },
    { title: '系統設定', description: 'Token 限制、分析 Agent 與表單配置', icon: Settings, path: '/admin/settings', roles: ['admin'] },
  ];
  const filtered = quickActions.filter((a) => a.roles.includes(user?.role));

  if (user?.role !== 'admin' && user?.role !== 'operator') return null;

  return (
    <AdminLayout>
      <div className="flex-1 overflow-y-auto grain-dark">
        {/* Hero header */}
        <header className="surface-glass-dark border-b border-atelier-line px-6 md:px-12 py-8 md:py-10 flex-shrink-0 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="atelier-caption text-atelier-coral mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-atelier-coral/60" />
              CONTROL · ROOM
              <span className="w-1.5 h-1.5 rounded-full bg-atelier-coral animate-pulse" />
            </div>
            <h1 className="font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              管理後台
            </h1>
            <p className="text-atelier-ink text-sm md:text-base mt-3 max-w-xl">
              系統管理與數據總覽 — 您擁有對整個 Reelscript AI 的完整控制權。
            </p>
          </div>
        </header>

        <main className="p-6 md:p-12 max-w-6xl mx-auto w-full">
          {loading ? (
            <div className="py-24 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-atelier-line border-t-lime-500 mx-auto mb-3" />
              <p className="atelier-caption">LOADING</p>
            </div>
          ) : (
            <>
              {/* Stats KPI strip */}
              <section className="mb-14">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-obsidian-600 border border-atelier-line rounded-2xl overflow-hidden">
                  {[
                    { k: '用戶總數', v: stats.totalUsers, icon: Users, tint: 'text-blue-400' },
                    { k: 'Agent 數量', v: stats.totalAgents, icon: Bot, tint: 'text-purple-400' },
                    { k: '案例數量', v: stats.totalCases, icon: BookOpen, tint: 'text-amber-400' },
                    { k: '系統狀態', v: 'ONLINE', icon: TrendingUp, tint: 'text-atelier-coral', isText: true },
                  ].map((m, i) => {
                    const Icon = m.icon;
                    return (
                      <div
                        key={i}
                        className="bg-atelier-surface p-6 md:p-8 relative group hover-glow transition-all"
                      >
                        <div className="flex items-start justify-between mb-6">
                          <Icon className={`w-5 h-5 ${m.tint}`} strokeWidth={1.75} />
                          <span className="atelier-caption">#{String(i + 1).padStart(2, '0')}</span>
                        </div>
                        <div className="atelier-caption mb-2">{m.k}</div>
                        <div className={`font-display font-semibold text-atelier-ink tracking-tighter tabular-nums leading-none ${m.isText ? 'text-2xl' : 'text-4xl md:text-5xl'}`}>
                          {m.isText ? (
                            <span className="text-atelier-coral">{m.v}</span>
                          ) : (
                            m.v
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Quick actions */}
              <section>
                <div className="flex items-end justify-between mb-6">
                  <div>
                    <div className="atelier-caption text-atelier-coral mb-3 flex items-center gap-3">
                      <span className="h-px w-8 bg-atelier-coral/60" />
                      QUICK · ACTIONS
                    </div>
                    <h2 className="font-display font-semibold text-atelier-ink text-2xl md:text-3xl tracking-tight">
                      快速操作
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-obsidian-600 border border-atelier-line rounded-2xl overflow-hidden">
                  {filtered.map((action, idx) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.path}
                        onClick={() => navigate(action.path)}
                        className="group relative bg-atelier-surface hover:bg-atelier-surface2 p-7 md:p-8 text-left transition-colors"
                        data-testid={`admin-quick-${action.path.replace(/\//g, '-')}`}
                      >
                        <div className="flex items-start justify-between mb-6">
                          <div className="w-10 h-10 rounded-md bg-white/[0.03] border border-atelier-line flex items-center justify-center group-hover:border-atelier-coral/40 group-hover:text-atelier-coral text-atelier-ink transition-all">
                            <Icon className="w-4 h-4" strokeWidth={1.75} />
                          </div>
                          <span className="atelier-caption">ACT · {String(idx + 1).padStart(2, '0')}</span>
                        </div>
                        <h3 className="font-display font-semibold text-atelier-ink text-lg md:text-xl mb-2 tracking-tight">
                          {action.title}
                        </h3>
                        <p className="text-atelier-ink text-sm leading-relaxed mb-5 line-clamp-2">
                          {action.description}
                        </p>
                        <div className="flex items-center justify-between">
                          {action.stat !== undefined ? (
                            <div className="flex items-baseline gap-1.5">
                              <span className="font-mono tabular-nums text-white text-base">{action.stat}</span>
                              <span className="atelier-caption">{action.statLabel}</span>
                            </div>
                          ) : (
                            <span className="atelier-caption">—</span>
                          )}
                          <div className="inline-flex items-center gap-1.5 atelier-caption group-hover:text-atelier-coral transition-colors">
                            <span>OPEN</span>
                            <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
